import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  setRingtoneDevicesData,
  setSelectedRingtoneDevice,
  setUserMediaError,
} from '../store/actions';
import {
  ringtoneDevicesDataSelector,
  selectedRingtoneDeviceSelector,
} from '../store/selectors';
import { message, notification } from 'antd';
import { defaultSelectedRingtoneDevice } from '../constants/common';

const changeDeviceInstruction =
  'Чтобы изменить устройство для рингтона, зайдите в настройки телефонии';

const baseGetUserMediaErrMsg =
  'Вы не сможете звонить, принимать звонки и выбирать устройства для воспроизведения рингтона.';

const browserSettingsText =
  'Чтобы исправить это, предоставьте нашему приложению доступ к микрофону в настройках браузера';

const getUserMediaErrors = {
  AbortError: {
    name: 'Неизвестная ошибка при попытке получить доступ к аудиоустройствам',
    description: baseGetUserMediaErrMsg,
  },
  NotAllowedError: {
    name: 'Доступ к аудиоустройствам запрещен',
    description: `${baseGetUserMediaErrMsg} ${browserSettingsText}`,
  },
  NotFoundError: {
    name: 'Аудиоустройства не найдены',
    description: baseGetUserMediaErrMsg,
  },
  NotReadableError: {
    name: 'Аппаратная ошибка, которая препятствует доступу к аудиоустройствам',
    description: baseGetUserMediaErrMsg,
  },
  SecurityError: {
    name: 'Настройки безопасности браузера ограничивают доступ к аудиоустройствам',
    description: `${baseGetUserMediaErrMsg} ${browserSettingsText}`,
  },
  default: {
    name: 'Ошибка при получении доступа к аудиоустройствам',
    description: baseGetUserMediaErrMsg,
  },
};

const enumerateRingtoneDevicesErrMsg = {
  enumerationFails:
    'Ошибка при получении списка устройств для воспроизведения рингтона',
  enumerateNotSupported:
    'Браузер не поддерживает метод определния устройств для воспроизведения рингтона',
  getUserMediaNotSupported:
    'Браузер не поддерживает метод для получения доступа к аудиоустройствам для воспроизведения рингтона',
};

const debounce = (func, delay = 200) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      func.apply(null, args);
    }, delay);
  };
};

const getAudioOutputDevices = (devices = []) =>
  devices.filter(
    (device) =>
      device.deviceId !== 'communications' && device.kind === 'audiooutput'
  );

const NotificationDesc = ({ label, withDefaultText = false }) => {
  return (
    <span>
      Рингтон будет воспроизводиться в{' '}
      <strong>{label.replace('По умолчанию - ', '')}</strong>{' '}
      {withDefaultText ? ' - устройство по умолчанию в вашей ОС' : ''}.{' '}
      {changeDeviceInstruction}
    </span>
  );
};

const openNotification = (data) => {
  notification.open(data);
};

const useRingtoneDevices = () => {
  const dispatch = useDispatch();

  const [notificationData, setNotificationData] = useState(null);

  const selectedDevice = useSelector(selectedRingtoneDeviceSelector);
  const { deviceList, error: enumerateDevicesErr } = useSelector(
    ringtoneDevicesDataSelector
  );

  const handleNotificationClose = () => {
    setNotificationData(null);
  };

  const dispatchRingtoneDevices = (data) =>
    dispatch(setRingtoneDevicesData(data));

  const updateDeviceList = debounce(() => {
    navigator.mediaDevices
      .enumerateDevices()
      .then((devices) => {
        const audioOutputDevices = getAudioOutputDevices(devices);
        dispatchRingtoneDevices({
          deviceList: audioOutputDevices,
          error: undefined,
        });
      })
      .catch((err) => {
        console.log('Ошибка при получении списка устройств:', err);
        dispatchRingtoneDevices({
          deviceList: [],
          error: enumerateRingtoneDevicesErrMsg.enumerationFails,
        });
      });
  });

  // Определяем устройства для воспроизведение рингтона
  useEffect(() => {
    const hasGetUserMedia = navigator.mediaDevices?.getUserMedia;
    const hasEnumerateDevices = navigator.mediaDevices?.enumerateDevices;

    let cleanupFunc;

    if (hasGetUserMedia && hasEnumerateDevices) {
      navigator.mediaDevices
        .getUserMedia({ audio: true })
        .then((stream) => {
          stream.getTracks().forEach((track) => {
            track.stop();
          });

          updateDeviceList();
          navigator.mediaDevices.addEventListener(
            'devicechange',
            updateDeviceList
          );

          cleanupFunc = () => {
            navigator.mediaDevices.removeEventListener(
              'devicechange',
              updateDeviceList
            );
          };
        })
        .catch((err) => {
          console.log('Ошибка при запросе доступа к аудио:', err, err.name);
          dispatchRingtoneDevices({ deviceList: [], error: undefined });
          dispatch(
            setUserMediaError(
              getUserMediaErrors[err.name] || getUserMediaErrors.default
            )
          );
        });
    } else {
      dispatchRingtoneDevices({
        deviceList: [],
        error: hasGetUserMedia
          ? enumerateRingtoneDevicesErrMsg.getUserMediaNotSupported
          : enumerateRingtoneDevicesErrMsg.enumerateNotSupported,
      });
    }

    return () => {
      if (typeof cleanupFunc === 'function') {
        cleanupFunc();
      }
    };
  }, []);

  // Выводим ошибку, если определение устройств не поддерживается или происходит ошибка
  useEffect(() => {
    if (enumerateDevicesErr) {
      message.error(enumerateDevicesErr);
    }
  }, [enumerateDevicesErr]);

  // Логика установки устройства для ригтона по умолчанию и вывод уведомлений
  useEffect(() => {
    const isDefault = selectedDevice.deviceId === 'default';

    const defaultDeviceChanged =
      isDefault &&
      deviceList.some(
        (device) =>
          device.deviceId === 'default' &&
          device.groupId !== selectedDevice.groupId
      );

    if (defaultDeviceChanged) {
      const defaultDevice = deviceList.find(
        (device) => device.deviceId === 'default'
      );

      dispatch(
        setSelectedRingtoneDevice(
          defaultDevice || defaultSelectedRingtoneDevice
        )
      );

      if (deviceList.length > 2) {
        setNotificationData({
          message: 'В системе изменено устройство по умолчанию',
          description: defaultDevice ? (
            <NotificationDesc label={defaultDevice.label} />
          ) : (
            ''
          ),
        });
      }
    } else {
      const currentDevice = deviceList.find(
        (device) => device.deviceId === selectedDevice.deviceId
      );

      if (!currentDevice) {
        const defaultDevice = deviceList.find(
          (device) => device.deviceId === 'default'
        );

        dispatch(
          setSelectedRingtoneDevice(
            defaultDevice || defaultSelectedRingtoneDevice
          )
        );

        if (deviceList.length > 2) {
          setNotificationData({
            message: 'Устройство для воспроизведения рингтона отключено',
            description: defaultDevice ? (
              <NotificationDesc label={defaultDevice.label} withDefaultText />
            ) : (
              ''
            ),
          });
        }
      }
    }
  }, [selectedDevice, deviceList, dispatch]);

  useEffect(() => {
    if (notificationData) {
      openNotification({
        ...notificationData,
        duration: 6,
        onClose: handleNotificationClose,
      });
    }
  }, [notificationData]);
};

export default useRingtoneDevices;
